import { useEffect } from 'react';

import type { VideoPlayer } from 'bundles/item-lecture';
// eslint-disable-next-line @coursera/custom-rules/no-private-bundle-imports
import { useDubbingContext } from 'bundles/item-lecture/providers/DubbingProvider';
import type { Track } from 'bundles/video-player/types/Track';
import userPreferences from 'bundles/video-player/utils/userPreferences';

type Props = {
  getTracks: () => Track[];
  defaultLanguageKey?: string;
  onSubtitleOffClick?: () => void;
  isDubbedAudioEnabled: boolean;
  onSubtitleLanguageChoiceClick?: (
    track: Track,
    disableTrackingForModernVideoPlayerExperiment?: boolean,
    isDubbedSubtitle?: boolean
  ) => void;
  player: VideoPlayer;
};

const useDubbingSubtitle = ({
  getTracks,
  defaultLanguageKey,
  onSubtitleOffClick,
  isDubbedAudioEnabled,
  onSubtitleLanguageChoiceClick,
  player,
}: Props) => {
  const { changeSubtitleOnAudioChange, setChangeSubtitleOnAudioChange } = useDubbingContext();
  useEffect(() => {
    // handle subtitle on audio change
    if (!changeSubtitleOnAudioChange.shouldChange) return;
    const nonDubbedSubtitleLanguage = userPreferences.get('subtitleLanguage');
    const nonDubbedTrack = getTracks().find((track: Track) => track.language === nonDubbedSubtitleLanguage);
    // user switches back to default language
    if (changeSubtitleOnAudioChange.languageCode === defaultLanguageKey) {
      // if subtitles were off in previous setting, then turn them off
      if (!nonDubbedTrack || nonDubbedSubtitleLanguage === 'none') {
        onSubtitleOffClick?.();
        return;
      }
      onSubtitleLanguageChoiceClick?.(nonDubbedTrack, true, false);
    } else {
      // user switches to dubbed language
      // if subtitles were off, don't change it
      if (!nonDubbedSubtitleLanguage || nonDubbedSubtitleLanguage === 'none') {
        return;
      }
      const selectedTrack = getTracks().find(
        (track: Track) => track.language === changeSubtitleOnAudioChange.languageCode
      );
      if (!selectedTrack) return;
      onSubtitleLanguageChoiceClick?.(selectedTrack, true, true);
    }
    setChangeSubtitleOnAudioChange({
      shouldChange: false,
      languageCode: undefined,
    });
  }, [
    changeSubtitleOnAudioChange.languageCode,
    changeSubtitleOnAudioChange.shouldChange,
    defaultLanguageKey,
    getTracks,
    onSubtitleLanguageChoiceClick,
    onSubtitleOffClick,
    setChangeSubtitleOnAudioChange,
  ]);

  useEffect(() => {
    // handle subtitle on page load
    // if there is any preferred dubbed subtitle language, set it as showing only if that dubbed audio is enabled
    // else do nothing and let initializeSubtitleTracks handle the rest in VideoPlayer.tsx
    player.on('loadedmetadata', () => {
      if (!isDubbedAudioEnabled) return;
      const preferredDubbedSubtitleLanguage = userPreferences.get('dubbedSubtitleLanguage');
      if (!preferredDubbedSubtitleLanguage) return;
      const selectedTrack = getTracks().find((track: Track) => track.language === preferredDubbedSubtitleLanguage);
      if (!selectedTrack) return;
      onSubtitleLanguageChoiceClick?.(selectedTrack, true, true);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDubbedAudioEnabled, getTracks, player]);
};

export { useDubbingSubtitle };
